import { Locale } from "@core/schema/Locale.js";
import { WebMessageCareerPosition } from "@core/schema/WebMessageCareerPosition.js";

export type CareerPositionIconKey = "code" | "design" | "test";

export interface CareerListItem {
	title: string;
	descItems?: string[];
	customNumber?: string;
}

export interface CareerBadge {
	text: string;
}

export interface CareerPosition {
	slug: string;
	iconKey: CareerPositionIconKey;
	title: string;
	perex: string;
	introduction: string;
	badges?: CareerBadge[];
	stackBadges?: string[];
	requirements: CareerListItem[];
	position: WebMessageCareerPosition;
	/** Kept in the codebase but not offered anywhere on the site — no card, no detail page, no sitemap entry. */
	hidden?: boolean;
}

export const careerPositionsByLocale: Record<Locale, CareerPosition[]> = {
	[Locale.cs]: [
		{
			slug: "solution-architect-delivery-lead",
			iconKey: "design",
			title: "Solution Architect / Delivery Lead",
			perex: "Hledáme cg[[Solution Architecta / Delivery Leada]]",
			introduction:
				"Portfolio nám roste rychleji než tým, který ho vede. Předimplementační fázi dnes drží CEO společně s Architect Leadem a Team Leadem — a hledáme dalšího člověka na tuhle úroveň, který si vezme zakázku za svou: od prvního jednání s klientem přes návrh řešení a rozsah až po naplánované sprinty a zadané tickety. Není to sales pozice ani čistý projekťák: na schůzce potřebujeme někoho, kdo klientovi rovnou rozebere problém, zeptá se na to, na co se ostatní nezeptají, a navrhne řešení, které dává technicky i ekonomicky smysl.",
			badges: [{ text: "Hybrid" }, { text: "HPP / IČO" }],
			stackBadges: ["TypeScript", "Svelte", "Node.js", "AWS", "Claude Code", "Scrum / Kanban"],
			requirements: [
				{
					title: "Co od tebe očekáváme?",
					descItems: [
						"wm[[Minimálně 5 let v IT]] na pozicích, kde jsi reálně navrhoval aplikace – ne jen hlídal termíny. Tohle je tvrdý požadavek.",
						"wm[[Návrh aplikací a architektury]] – rozpadneš zadání na datový model, integrace a milníky. Obhájíš, proč zrovna takhle, a znáš cenu svých rozhodnutí.",
						"wm[[Kritické myšlení]] – zadání nepřebíráš, ale rozporuješ. Poznáš, když si klient říká o něco, co jeho problém nevyřeší.",
						"wm[[Analýza přímo na schůzce]] – nepotřebuješ týden, abys klientovi řekl první návrh směru.",
						"wm[[Vedení lidí]] – plánuješ sprinty, píšeš tickety, které jsou zadáním a ne hádankou, a umíš dát zpětnou vazbu vývojářům i AI engineerům.",
						"wm[[Střízlivý pohled na AI]] – víš, kde ti agent ušetří den a kde ti ho sebere. Umíš zadat práci AI engineerovi, výstup zkontrolovat a nepustit dál něco, čemu nerozumíš. Nehledáme nadšence ani odpůrce, ale člověka s vlastní zkušeností.",
						"wm[[Reprezentativní vystupování]] – zvládneš jednání s majitelem firmy i s jeho IT oddělením a v obou případech mluvíš jejich jazykem.",
						"wm[[Komunikace rozpočtu a rozsahu]] – umíš říct, co se za dané peníze stihne a co ne, a udržet to. Prodávat nemusíš, to není tvoje práce.",
					],
				},
				{
					title: "Výhodou (nevyžadujeme, ale oceníme)",
					descItems: [
						"wm[[Praktická zkušenost s Claude Code]] nebo jiným agentic nástrojem – jednodušší tickety si odbavíš sám a nečekáš na kapacitu týmu.",
						"Zkušenost se zadáváním práce AI engineerům a realistická představa, co od agentů čekat a co ne.",
						"Znalost našeho stacku (TypeScript, Svelte, Node.js, AWS) nebo ochota se do něj rychle dostat.",
						"Zkušenost s integracemi na ERP, platební brány a fakturaci.",
						"Angličtina na úrovni, kdy zvládneš jednání i dokumentaci.",
					],
				},
				{
					title: "Co nabízíme?",
					descItems: [
						"wm[[Místo v zavedeném lead týmu]] – na návrh řešení nejsi sám. V předimplementační fázi s tebou sedí Architect Lead i Team Lead. Ty držíš zakázku a rozhoduješ o ní, oni ti kryjí záda v technických detailech.",
						"Finanční ohodnocení odpovídající seniorní lead roli, individuálně podle toho, co přineseš. Samozřejmostí je osobní hodnotící složka.",
						"Výkonný stroj (MacBook / PC) a nástroje, co k práci potřebuješ – včetně placených AI nástrojů.",
						"Hybridní režim – část času v kanceláři kvůli schůzkám s klienty, zbytek odkud chceš.",
						"Žádný legacy bordel – moderní stack, CI/CD, monitoring v Sentry.",
						"Spolupráce na HPP fulltime nebo IČO.",
					],
				},
			],
			position: WebMessageCareerPosition.Manager,
		},
		{
			slug: "fullstack-developer-mid-senior",
			iconKey: "code",
			title: "Medior / Senior Fullstack Developer",
			perex: "Hledáme cg[[Medior / Senior Fullstack Developera]]",
			introduction:
				"Děláme produkty na míru pro klienty, kde standardní krabicové řešení nestačí — od B2B/B2C e-shopů přes interní systémy až po integrace s ERP, platebními bránami a fakturací. Hledáme zkušeného fullstack vývojáře, který si umí vzít feature od zadání po produkci, rozumí trade-offům a nepotřebuje, aby ho někdo držel za ruku. U nás se cení proaktivní přístup, schopnost přijít s návrhem řešení a ne jen čekat na úkol.",
			badges: [{ text: "Hybrid / Remote" }, { text: "HPP / IČO" }, { text: "50 000 – 100 000 Kč" }],
			stackBadges: ["Svelte", "TypeScript", "Node.js", "AWS", "GraphQL", "Tailwind"],
			requirements: [
				{
					title: "Co od tebe očekáváme?",
					descItems: [
						"wm[[Praxe na pozici fullstack vývojáře]] – ideálně 3+ roky na produkčních projektech.",
						"wm[[TypeScript ve striktním režimu]] – víš, proč `any` smrdí a kdy to vadí.",
						"wm[[Frontend ve Svelte / SvelteKit]] (nebo React / Vue / Angular se silnou ochotou přejít na Svelte).",
						"wm[[Node.js na backendu]] a praktická zkušenost s některým cloudem – ideálně AWS (Lambda, DynamoDB, AppSync, CloudFront).",
						"wm[[Návrh a integrace API]] – REST i GraphQL, schémata, validace.",
						"wm[[Smysl pro byznys logiku]] – chápeš, že feature není hotová, dokud nepokrývá reálné edge cases.",
						"wm[[Proaktivní přístup]] – přijdeš s návrhem, ne jen s otázkou. Bereš odpovědnost za to, co dodáš.",
					],
				},
				{
					title: "Výhodou (nevyžadujeme, ale oceníme)",
					descItems: [
						"wm[[Zkušenost s Agentic Engineeringem]] – umíš s LLM agenty pracovat jako s nástrojem, ne jako berlou. Nehledáme vibecodery — chceme někoho, kdo kód po agentovi přečte, opraví a rozumí mu.",
						"Infrastruktura jako kód (CloudFormation, Terraform).",
						"Zkušenost s integracemi (Fakturoid, GoPay, ČSOB GAPA, SAP, Algolia apod.).",
						"Praktická zkušenost s mobilními technologiemi (React Native, Ionic).",
					],
				},
				{
					title: "Co nabízíme?",
					descItems: [
						"wm[[Finanční ohodnocení 50 000 – 100 000 Kč]] dle seniority a praxe (HPP nebo IČO). Uvedené rozpětí je výchozí — ke každému přistupujeme individuálně a samozřejmostí je osobní hodnotící složka. Nejsme korporát, kvalitní práci a zápal v žilách umíme ocenit.",
						"Výkonný stroj (MacBook / PC) a nástroje, co k práci potřebuješ.",
						"Flexibilitu – kancelář, hybrid nebo full remote.",
						"Reálný vliv na architekturu a podobu produktů, které používají tisíce uživatelů.",
						"Žádný legacy bordel – moderní stack, CI/CD, monitoring v Sentry.",
						"Spolupráce na HPP fulltime nebo IČ parttime.",
					],
				},
			],
			position: WebMessageCareerPosition.Developer,
		},
		{
			slug: "fullstack-developer-junior",
			iconKey: "code",
			title: "Junior fullstack vývojář",
			perex: "Hledáme cg[[Junior fullstack vývojáře]]",
			introduction:
				"Chceš se naučit dělat věci pořádně? Vezmeme tě pod křídla seniorů a budeš pracovat na reálných produktech, ne na umělých školních zadáních. Vyžadujeme alespoň rok praxe v oboru — potřebujeme, abys měl/a základ a věděl/a, do čeho jdeš. U nás se cení proaktivní přístup, schopnost zeptat se, když něco nevíš, a chuť posouvat se dál.",
			badges: [{ text: "Hybrid / Remote" }, { text: "HPP / IČO" }, { text: "30 000 – 50 000 Kč" }],
			stackBadges: ["Svelte", "TypeScript", "Node.js", "Tailwind"],
			requirements: [
				{
					title: "Co od tebe očekáváme?",
					descItems: [
						"wm[[Minimálně 1 rok praxe v oboru]] – komerční projekt, stáž, freelance, vlastní produkt v produkci.",
						"wm[[Solidní základy TypeScriptu]] – typy nejsou jen ozdoba.",
						"wm[[Zkušenost s některým moderním frontendem]] (Svelte, React, Vue nebo Angular) a chuť se učit Svelte.",
						"wm[[Základní orientace v Node.js]] a HTTP / API.",
						"wm[[Proaktivní přístup]] – zeptáš se, hledáš odpověď, navrhneš řešení. Nečekáš, až ti někdo přidělí ticket.",
						"Schopnost přijímat feedback a iterovat – code review není útok.",
					],
				},
				{
					title: "Výhodou (nevyžadujeme, ale oceníme)",
					descItems: [
						"wm[[Zkušenost s Agentic Engineeringem]] – umíš LLM agenty používat efektivně. Nechceme vibecodery — chceme, abys rozuměl/a tomu, co ti agent napsal, a uměl/a to obhájit.",
						"Praktická zkušenost s cloudem (AWS / GCP / Azure).",
						"Vlastní pet projekt, ke kterému se hrdě hlásíš.",
					],
				},
				{
					title: "Co nabízíme?",
					descItems: [
						"wm[[Finanční ohodnocení 30 000 – 50 000 Kč]] (HPP nebo IČO), individuálně dle zkušeností. Uvedené rozpětí je výchozí — ke každému přistupujeme individuálně a samozřejmostí je osobní hodnotící složka. Nejsme korporát, kvalitní práci a zápal v žilách umíme ocenit.",
						"Mentoring od seniorů, code review na každé PR, prostor na vlastní iniciativu.",
						"Moderní stack (Svelte, TypeScript, AWS serverless) – budeš se učit věci, co se počítají.",
						"Flexibilitu – kancelář, hybrid nebo full remote.",
						"Reálné produkty pro reálné klienty, ne školní úkoly.",
					],
				},
			],
			position: WebMessageCareerPosition.Developer,
			hidden: true,
		},
	],

	[Locale.en]: [
		{
			slug: "solution-architect-delivery-lead",
			iconKey: "design",
			title: "Solution Architect / Delivery Lead",
			perex: "We’re looking for a cg[[Solution Architect / Delivery Lead]]",
			introduction:
				"Our portfolio is growing faster than the team that leads it. The pre-implementation phase is currently held by the CEO together with our Architect Lead and Team Lead — and we’re looking for one more person at that level, someone who takes a project and owns it: from the first client meeting through solution design and scope all the way to planned sprints and written tickets. This is neither a sales role nor pure project management: in a meeting we need someone who can break the client’s problem apart on the spot, ask what nobody else asks, and propose a solution that makes sense technically and commercially.",
			badges: [{ text: "Hybrid" }, { text: "Full-time / Contractor" }],
			stackBadges: ["TypeScript", "Svelte", "Node.js", "AWS", "Claude Code", "Scrum / Kanban"],
			requirements: [
				{
					title: "What we expect from you",
					descItems: [
						"wm[[At least 5 years in IT]] in roles where you actually designed applications – not just tracked deadlines. This one is non-negotiable.",
						"wm[[Application and architecture design]] – you break a brief down into a data model, integrations, and milestones. You can defend why it should be built that way, and you know what your decisions cost.",
						"wm[[Critical thinking]] – you challenge a brief instead of just passing it on. You spot when a client is asking for something that won’t solve their problem.",
						"wm[[Analysis on the spot]] – you don’t need a week before you can give a client a first direction.",
						"wm[[Leading people]] – you plan sprints, write tickets that are briefs rather than riddles, and give feedback to developers and AI engineers alike.",
						"wm[[A level-headed view of AI]] – you know where an agent saves you a day and where it costs you one. You can hand work to an AI engineer, review the output, and refuse to ship something you don’t understand. We’re not after enthusiasts or sceptics, but someone with first-hand experience.",
						"wm[[Presence in front of clients]] – you can handle a meeting with a company owner and with their IT department, speaking each one’s language.",
						"wm[[Budget and scope communication]] – you can say what fits the budget and what doesn’t, and hold that line. Selling isn’t your job.",
					],
				},
				{
					title: "Nice to have",
					descItems: [
						"wm[[Hands-on experience with Claude Code]] or another agentic tool – you clear the simpler tickets yourself instead of waiting for team capacity.",
						"Experience handing work to AI engineers and a realistic sense of what agents can and can’t do.",
						"Familiarity with our stack (TypeScript, Svelte, Node.js, AWS) or the drive to pick it up quickly.",
						"Experience with ERP, payment gateway, and invoicing integrations.",
						"English good enough for both client meetings and documentation.",
					],
				},
				{
					title: "What we offer",
					descItems: [
						"wm[[A seat in an established lead team]] – you don’t design solutions alone. The Architect Lead and Team Lead sit with you through pre-implementation. You own the project and make the calls; they cover you on the technical detail.",
						"Compensation matching a senior lead role, set individually according to what you bring. A personal performance component goes without saying.",
						"A powerful workstation (MacBook / PC) and the tools you need – paid AI tools included.",
						"A hybrid setup – part of your time in the office for client meetings, the rest wherever you want.",
						"No legacy mess – modern stack, CI/CD, Sentry monitoring.",
						"Full-time employment or contractor cooperation.",
					],
				},
			],
			position: WebMessageCareerPosition.Manager,
		},
		{
			slug: "fullstack-developer-mid-senior",
			iconKey: "code",
			title: "Medior / Senior Fullstack Developer",
			perex: "We’re looking for a cg[[Medior / Senior Fullstack Developer]]",
			introduction:
				"We build custom products for clients where off-the-shelf isn’t enough — B2B/B2C e-shops, internal systems, integrations with ERPs, payment gateways, and invoicing. We’re looking for an experienced fullstack developer who can take a feature from spec to production, understands trade-offs, and doesn’t need hand-holding. We value a proactive approach — bring proposals, not just questions.",
			badges: [{ text: "Hybrid / Remote" }, { text: "Full-time / Contractor" }, { text: "50,000 – 100,000 CZK" }],
			stackBadges: ["Svelte", "TypeScript", "Node.js", "AWS", "GraphQL", "Tailwind"],
			requirements: [
				{
					title: "What we expect from you",
					descItems: [
						"Experience as a fullstack developer – ideally 3+ years on production projects.",
						"TypeScript in strict mode – you know why `any` smells and when it matters.",
						"Frontend in Svelte / SvelteKit (or React / Vue / Angular with willingness to switch to Svelte).",
						"Node.js on the backend and hands-on cloud experience – ideally AWS (Lambda, DynamoDB, AppSync, CloudFront).",
						"API design and integration – REST and GraphQL, schemas, validation.",
						"A sense for business logic – a feature isn’t done until it handles the real edge cases.",
						"Proactive attitude – you propose, not just ask. You own what you ship.",
					],
				},
				{
					title: "Nice to have",
					descItems: [
						"Experience with Agentic Engineering – you use LLM agents as a tool, not a crutch. No vibecoders — we want someone who reads, fixes, and understands the agent’s output.",
						"Infrastructure as code (CloudFormation, Terraform).",
						"Integration experience (Fakturoid, GoPay, ČSOB GAPA, SAP, Algolia, etc.).",
						"Hands-on mobile experience (React Native, Ionic).",
					],
				},
				{
					title: "What we offer",
					descItems: [
						"Compensation 50,000 – 100,000 CZK based on seniority and experience (full-time or contractor). The range is just a starting point — we approach everyone individually and a personal performance bonus is a given. We’re not a corporation; we know how to reward quality work and real drive.",
						"A powerful workstation (MacBook / PC) and the tools you need.",
						"Flexibility – office, hybrid, or full remote.",
						"Real influence on architecture and products used by thousands of users.",
						"No legacy mess – modern stack, CI/CD, Sentry monitoring.",
						"Full-time employment or part-time contractor cooperation.",
					],
				},
			],
			position: WebMessageCareerPosition.Developer,
		},
		{
			slug: "fullstack-developer-junior",
			iconKey: "code",
			title: "Junior Fullstack Developer",
			perex: "We’re looking for a Junior Fullstack Developer",
			introduction:
				"Want to learn how to build things properly? We’ll put you under the wing of our seniors and you’ll work on real products, not contrived exercises. We require at least one year of hands-on experience — you need a foundation and an idea of what you’re getting into. We value a proactive attitude, asking when you don’t know, and the drive to keep growing.",
			badges: [{ text: "Hybrid / Remote" }, { text: "Full-time / Contractor" }, { text: "30,000 – 50,000 CZK" }],
			stackBadges: ["Svelte", "TypeScript", "Node.js", "Tailwind"],
			requirements: [
				{
					title: "What we expect from you",
					descItems: [
						"At least 1 year of practical experience – commercial project, internship, freelance, or your own shipped product.",
						"Solid TypeScript fundamentals – types aren’t decoration.",
						"Experience with a modern frontend framework (Svelte, React, Vue, or Angular) and willingness to learn Svelte.",
						"Basic familiarity with Node.js and HTTP / APIs.",
						"Proactive attitude – you ask, you investigate, you propose. You don’t wait for tickets.",
						"Ability to take feedback and iterate – code review isn’t an attack.",
					],
				},
				{
					title: "Nice to have",
					descItems: [
						"Experience with Agentic Engineering – you can use LLM agents effectively. No vibecoders — we want you to understand what the agent produced and be able to defend it.",
						"Hands-on cloud experience (AWS / GCP / Azure).",
						"A pet project you’re proud of.",
					],
				},
				{
					title: "What we offer",
					descItems: [
						"Compensation 30,000 – 50,000 CZK (full-time or contractor), individually based on experience. The range is just a starting point — we approach everyone individually and a personal performance bonus is a given. We’re not a corporation; we know how to reward quality work and real drive.",
						"Mentoring from seniors, code review on every PR, room for your own initiative.",
						"Modern stack (Svelte, TypeScript, AWS serverless) – you’ll learn things that matter.",
						"Flexibility – office, hybrid, or full remote.",
						"Real products for real clients, not school assignments.",
					],
				},
			],
			position: WebMessageCareerPosition.Developer,
			hidden: true,
		},
	],
};

export function getVisibleCareerPositions(locale: Locale): CareerPosition[] {
	return careerPositionsByLocale[locale].filter((position) => position.hidden !== true);
}
